import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import StyledLink from 'components/StyledLink';
import _ from 'lodash';
import { Item } from './components';
import {
  INCOMPLETE_ATTEMPT,
  NOT_ATTEMPTED,
  INSTRUCTOR_UNSCORED,
} from '../scoreReason';

const isIncomplete = (reason) =>
  [INCOMPLETE_ATTEMPT, NOT_ATTEMPTED].includes(reason);

const getAttempt = (result) =>
  _.get(
    result,
    'cohortById.enrollmentByStudent.getLatestAttemptStudentManualGradeReport'
  );

const getGrade = (result) => {
  const attempt = getAttempt(result);
  return (
    attempt.grades &&
    attempt.grades.length > 0 &&
    attempt.grades.sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
    )[0].score
  );
};

const getStatus = (result) => getAttempt(result).state;

const componentGradeQuery = gql`
  query queryGetGrades(
    $cohortId: UUID!
    $deploymentId: UUID!
    $interactiveId: UUID!
    $componentId: UUID!
    $studentId: UUID!
  ) {
    cohortById(cohortId: $cohortId) {
      id
      enrollmentByStudent(studentId: $studentId) {
        accountId
        getLatestAttemptStudentManualGradeReport(
          input: {
            componentId: $componentId
            deploymentId: $deploymentId
            parentId: $interactiveId
            parentType: INTERACTIVE
          }
        ) {
          studentId
          attemptId
          componentId
          state
          parentType
          grades {
            id
            score
            instructorId
            createdAt
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

const GradableComponentRow = ({
  cohortId,
  componentId,
  title,
  deploymentId,
  interactiveId,
  studentId,
  maxScore,
}) => {
  const { data, loading, error } = useQuery(componentGradeQuery, {
    variables: {
      cohortId,
      deploymentId,
      componentId,
      interactiveId,
      studentId,
    },
  });

  const link = `/classes/${cohortId}/review/${deploymentId}/${interactiveId}/${componentId}/${studentId}`;
  let gradeContent = '\u2026';
  let showLink = true;

  if (error) {
    throw new Error('Failed to retrieve student score attempt');
  }

  if (!loading) {
    const grade = getGrade(data);
    const status = getStatus(data);

    if (isIncomplete(status)) {
      gradeContent = `\u2014/${maxScore}`;
      showLink = false;
    } else if (status === INSTRUCTOR_UNSCORED) {
      gradeContent = 'Grade';
    } else {
      gradeContent = `${grade}/${maxScore}`;
    }
  }

  return (
    <Item key={componentId}>
      <span>{title}</span>
      {showLink ? (
        <StyledLink to={link}>{gradeContent}</StyledLink>
      ) : (
        <span>{gradeContent}</span>
      )}
    </Item>
  );
};

GradableComponentRow.propTypes = {
  cohortId: PropTypes.string.isRequired,
  componentId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  deploymentId: PropTypes.string.isRequired,
  interactiveId: PropTypes.string.isRequired,
  studentId: PropTypes.string.isRequired,
  maxScore: PropTypes.number.isRequired,
};

export default GradableComponentRow;
